/**
 * Alpha OPS: full-screen 3D vault (outside the dashboard shell). Black screen with
 * the Alpha mark brightening as assets load -> the mark switches off like a light ->
 * the red neon strikes and the closed vault comes up out of the dark. ALPHA OPS is
 * painted on the left wall in the scene; on the right wall, level with the Alpha mark,
 * a hold-to-unlock control. Holding it charges the door; once full the vault unlocks
 * and swings open, and "Coming soon" with the waitlist form comes up in the opening.
 * (The room behind the door comes later.) Back returns to /dashboard.
 */
import './AlphaOps.css';

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AlphaMark from './AlphaMark';
import HoldToUnlock from './HoldToUnlock';
import { createVault } from './vault';
import WaitlistForm, { JoinedNote } from './WaitlistForm';

const LOGO_OFF_MS = 950; // matches the aops-switch-off keyframes

export default function AlphaOps() {
  const hostRef = useRef(null);
  const holdRef = useRef(null);
  const formRef = useRef(null);
  const vaultRef = useRef(null);
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('loading');
  const [joined, setJoined] = useState(false);

  // nothing else in the app sets the title, so hand the old one back on the way out
  useEffect(() => {
    const prev = document.title;
    document.title = 'Alpha OPS · Alpha Capital';
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let switchTimer = 0;
    const vault = createVault(
      host,
      {
        onProgress: (f) => setProgress((p) => Math.max(p, f)),
        onReady: () => {
          setProgress(1);
          setPhase('switching');
          switchTimer = window.setTimeout(
            () => {
              setPhase('intro');
              vault.start();
            },
            reducedMotion ? 0 : LOGO_OFF_MS,
          );
        },
        onIntroDone: () => setPhase((p) => (p === 'intro' ? 'idle' : p)),
        onOpened: () => setPhase('open'),
        onError: (err) => {
          console.error('[alpha-ops] failed to load the vault', err);
          setPhase('error');
        },
      },
      { reducedMotion, anchorEl: holdRef.current, formEl: formRef.current },
    );
    vaultRef.current = vault;
    return () => {
      window.clearTimeout(switchTimer);
      vaultRef.current = null;
      vault.dispose();
    };
  }, []);

  return (
    <div className={`aops-root is-${phase}`}>
      <div className="aops-stage" ref={hostRef} />
      <h1 className="aops-sr-only">Alpha OPS</h1>
      {/* phones: the painted wordmark is on a wall that's off-screen, so it sits over the scene */}
      <p className="aops-wordmark" aria-hidden="true">
        ALPHA<span>OPS</span>
      </p>

      <HoldToUnlock
        ref={holdRef}
        disabled={phase !== 'intro' && phase !== 'idle' && phase !== 'unlocking'}
        onCharge={(p) => vaultRef.current?.setCharge(p)}
        onComplete={() => {
          setPhase('unlocking');
          vaultRef.current?.unlock();
        }}
      />

      <div className="aops-open" ref={formRef} aria-hidden={phase !== 'open'} inert={phase !== 'open' ? '' : undefined}>
        <span className="aops-soon">
          <AlphaMark className="aops-soon-mark" />
          Coming soon
        </span>
        <h2 aria-label="Alpha OPS">
          ALPHA <span className="aops-accent">OPS</span>
        </h2>
        {joined ? (
          <JoinedNote>You’re on the list. We’ll let you know when Alpha OPS launches.</JoinedNote>
        ) : (
          <WaitlistForm
            disabled={phase !== 'open'}
            placeholder="Enter your email"
            submitLabel="Be first to know"
            busyLabel="Sending…"
            note="Get notified when Alpha OPS launches."
            onJoined={() => setJoined(true)}
          />
        )}
      </div>

      <button type="button" className="aops-back" onClick={() => navigate('/dashboard')}>
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M9.75 3.5 5.25 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Dashboard</span>
      </button>

      <div
        className="aops-loader"
        role="status"
        aria-live="polite"
        aria-hidden={phase !== 'loading' && phase !== 'switching' && phase !== 'error'}
        style={{ '--aops-progress': progress }}
      >
        <div className="aops-loader-mark">
          <AlphaMark className="aops-loader-logo" />
          <AlphaMark className="aops-loader-logo aops-loader-logo--glow" />
        </div>
        {phase === 'error' ? (
          <p className="aops-loader-text">The vault couldn’t load. Check your connection and try again.</p>
        ) : (
          <span className="aops-sr-only">Loading Alpha OPS, {Math.round(progress * 100)}%</span>
        )}
      </div>
    </div>
  );
}
